<template>
  <div>
    <global-confirm-box ref="confirmBox"></global-confirm-box>

    <page-header
      :title="$t('menu_list')"
      :subtitle="$t('manage_menu_types')"
      icon="mdi-food-variant"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button :disabled="loading.menuTypes" @click="openCreateDialog">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_menu_type") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="search"
      :search-placeholder="$t('search_menu_types_placeholder')"
      searchable
      :loading="loading.menuTypes"
    />

    <MenuTypeDialog
      v-model="dialog"
      :menuTypes="menuTypes"
      :menuType="editedItem"
      :is-edit="!!editedItem.id"
      @menu-type-created="handleMenuTypeCreated"
      @menu-type-updated="handleMenuTypeUpdated"
      @close="handleDialogClose"
    />

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="processedMenuTypes"
          :loading="loading.menuTypes"
          class="striped-table ordera-box-shadow"
          mobile-breakpoint="0"
          show-expand
          loading-text="Loading menu types..."
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <template v-if="item.children && item.children.length">
              <v-btn
                color="primary"
                dark
                small
                rounded
                @click="expand(true)"
                v-if="!isExpanded"
              >
                {{ $t("show") }}
              </v-btn>
              <v-btn color="primary" dark small rounded @click="expand(false)" v-else>
                {{ $t("hide") }}
              </v-btn>
            </template>
          </template>
          <template v-slot:expanded-item="{ item }">
            <template v-if="item.children">
              <td :colspan="headers.length" class="px-0">
                <v-data-table
                  dense
                  :headers="headers"
                  class="ordera-box-shadow striped-table"
                  :items="item.children || []"
                  hide-default-footer
                  hide-default-header
                >
                  <template v-slot:item.actions="{ item, index }">
                    <v-row no-gutters justify="end">
                      <edit-button
                        class="mr-2"
                        @click="editMenuType(item)"
                        :disabled="loading.deleting || loading.updating[item.id]"
                      ></edit-button>
                      <remove-button
                        @click="deleteMenuType(item)"
                        :loading="loading.deleting"
                        :disabled="loading.deleting || loading.updating[item.id]"
                      ></remove-button>
                    </v-row>
                  </template>
                </v-data-table>
              </td>
            </template>
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name }} ({{ item.code }})
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end" v-if="item.restaurant_id">
              <edit-button
                class="mr-2"
                @click="editMenuType(item)"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
              <remove-button
                @click="deleteMenuType(item)"
                :loading="loading.deleting"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
            </v-row>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="processedMenuTypes"
          :loading="loading.menuTypes"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="3"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name }}
          </template>
          <template v-slot:item.actions="{ item }">
            <template v-if="item.restaurant_id">
              <edit-button
                class="mr-2"
                @click="editMenuType(item)"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
              <remove-button
                @click="deleteMenuType(item)"
                :loading="loading.deleting"
                :disabled="loading.deleting || loading.updating[item.id]"
              />
            </template>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn
          color="primary"
          block
          :disabled="loading.menuTypes"
          @click="openCreateDialog"
        >
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_menu_type") }}
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import { defaultMenuType } from "@/models/menuType";
import hasSnackbar from "@/mixins/hasSnackbar";
import menuService from "@/services/menu";
import MenuTypeDialog from "@/components/menus/dialogs/MenuTypeDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";

export default {
  mixins: [hasSnackbar],
  components: {
    MenuTypeDialog,
    PageHeader,
    PageToolbar,
    MobileDataCards,
  },

  data: () => ({
    dialog: false,
    search: "",
    menuTypes: [],
    editedItem: {},
    loading: {
      menuTypes: false,
      deleting: false,
      updating: {},
    },
  }),
  computed: {
    headers() {
      return [
        { text: "ID", align: "start", value: "id", width: "100px" },
        { text: this.$t("title"), align: "start", value: "name" },
        { text: this.$t("actions"), value: "actions", sortable: false, align: "right" },
        { text: "", value: "data-table-expand" },
      ];
    },
    mobileHeaders() {
      return [
        { text: "ID", value: "id", cols: 2 },
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("actions"), value: "actions", cols: 4 },
      ];
    },
    processedMenuTypes() {
      return this.customSearch(
        this.menuTypes.map((type) => {
          return {
            ...type,
            code: type.name,
            name: this.$t(type.name) || type.name,
          };
        }),
        this.headers,
        this.search
      );
    },
  },

  created() {
    this.loadMenuTypes();
  },

  methods: {
    findMenuTypeById(menuTypeId, menuTypes = this.menuTypes) {
      for (let menuType of menuTypes) {
        if (menuType.id === menuTypeId) {
          return menuType;
        }
        if (menuType.children) {
          const found = this.findMenuTypeById(menuTypeId, menuType.children);
          if (found) return found;
        }
      }
      return null;
    },

    /**
     * Loads all menu types from the API
     */
    loadMenuTypes() {
      this.loading.menuTypes = true;

      menuService
        .getMenuTypes()
        .then((res) => {
          this.menuTypes = res.data;
        })
        .catch((error) => {
          console.error("Error loading menu types:", error);
          this.showErrorSnackbar(this.$t("error_loading_menu_types"));
        })
        .finally(() => {
          this.loading.menuTypes = false;
        });
    },
    editMenuType(item) {
      // Create a clean copy for editing
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteMenuType(item) {
      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("confirm_delete_menu"), {
          color: "error",
        })
        .then(() => {
          this.loading.deleting = true;

          menuService
            .deleteMenuType(item.id)
            .then((res) => {
              this.removeMenuTypeFromList(item);
              this.showSuccessSnackbar(this.$t("menu_type_deleted"));
            })
            .catch((error) => {
              console.warn("Error deleting menu type:", error);
              this.showErrorSnackbar(this.$t("error_deleting_menu_type"));
            })
            .finally(() => {
              this.loading.deleting = false;
            });
        })
        .catch((err) => console.warn(err));
    },

    removeMenuTypeFromList(item) {
      if (item.parent_id) {
        // Find parent menu type and remove child
        const parentMenuType = this.menuTypes.find((mt) => mt.id === item.parent_id);
        if (parentMenuType?.children) {
          const childIndex = parentMenuType.children.findIndex(
            (child) => child.id === item.id
          );
          if (childIndex > -1) {
            parentMenuType.children.splice(childIndex, 1);
          }
        }
      } else {
        // Remove from main menu types list
        const menuTypeIndex = this.menuTypes.findIndex((mt) => mt.id === item.id);
        if (menuTypeIndex > -1) {
          this.menuTypes.splice(menuTypeIndex, 1);
        }
      }
    },

    openCreateDialog() {
      this.editedItem = { ...defaultMenuType };
      this.dialog = true;
    },

    handleMenuTypeCreated(menuType) {
      this.addMenuTypeToList(menuType);
    },

    addMenuTypeToList(menuType) {
      if (menuType.parent_id) {
        // Add to parent's children
        const parentMenuType = this.menuTypes.find((mt) => mt.id === menuType.parent_id);
        if (parentMenuType) {
          if (!parentMenuType.children) {
            this.$set(parentMenuType, "children", []);
          }
          parentMenuType.children.push(menuType);
        }
      } else {
        // Add to main menu types list
        this.menuTypes.push(menuType);
      }
    },

    handleMenuTypeUpdated(menuType) {
      this.updateMenuTypeInList(menuType);
    },

    updateMenuTypeInList(menuType) {
      if (menuType.parent_id) {
        // Update child menu type
        const parentMenuType = this.menuTypes.find((mt) => mt.id === menuType.parent_id);
        if (parentMenuType?.children) {
          const childIndex = parentMenuType.children.findIndex(
            (child) => child.id === menuType.id
          );
          if (childIndex > -1) {
            Object.assign(parentMenuType.children[childIndex], menuType);
          }
        }
      } else {
        // Update main menu type
        const menuTypeIndex = this.menuTypes.findIndex((mt) => mt.id === menuType.id);
        if (menuTypeIndex > -1) {
          Object.assign(this.menuTypes[menuTypeIndex], menuType);
        }
      }
    },

    handleDialogClose() {
      this.editedItem = {};
    },
  },
};
</script>
<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
